import React, { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Crosshair, MapPinned, Pencil, Plus, Wind } from "lucide-react";
import { Button } from "../components/Button";
import {
  InteractiveLakeMap,
  type LakeMapSwim,
} from "../components/InteractiveLakeMap";

function swimSourceLabel(source?: string) {
  if (source === "verified_owner") return "Verified by CatchCompass owner";
  if (source === "verified_community") return "Community submission · approved";
  if (source === "verified_seed") return "Known venue peg";
  if (source === "pending_review") return "Your private peg · awaiting review";
  if (source === "review_rejected") return "Your private peg";
  if (source === "private") return "Your private peg";
  if (source === "history") return "From your fishing history";
  return "Known peg";
}

function hasGps(swim: LakeMapSwim) {
  return (
    typeof swim.latitude === "number" &&
    Number.isFinite(swim.latitude) &&
    typeof swim.longitude === "number" &&
    Number.isFinite(swim.longitude)
  );
}

function privateSwimId(swim?: LakeMapSwim | null) {
  if (!swim?.can_edit_personal || !swim.id.startsWith("saved:")) return null;
  const value = Number(swim.id.slice("saved:".length));
  return Number.isFinite(value) && value > 0 ? value : null;
}

function currentGps() {
  return new Promise<{
    latitude: number;
    longitude: number;
    accuracy_m: number | null;
    captured_at: string;
  }>((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("This device does not provide GPS location to the browser."));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) =>
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy_m: Number.isFinite(position.coords.accuracy)
            ? position.coords.accuracy
            : null,
          captured_at: new Date(position.timestamp || Date.now()).toISOString(),
        }),
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          reject(
            new Error(
              "Location permission is off. Allow location for CatchCompass in Safari/iPhone settings, then try again.",
            ),
          );
          return;
        }
        if (error.code === error.TIMEOUT) {
          reject(new Error("GPS took too long to lock on. Try again while you are outside by the peg."));
          return;
        }
        reject(new Error("Your current GPS position could not be read. Try again in a moment."));
      },
      {
        enableHighAccuracy: true,
        maximumAge: 0,
        timeout: 15_000,
      },
    );
  });
}

export default function LakeMapPage() {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [editingSwim, setEditingSwim] = useState<LakeMapSwim | null>(null);
  const [showPersonalEditor, setShowPersonalEditor] = useState(false);
  const [pegName, setPegName] = useState("");
  const [gpsMessage, setGpsMessage] = useState("");

  const requestedHighlight = useMemo(
    () => new URLSearchParams(window.location.search).get("peg"),
    [],
  );

  const mapQuery = useQuery({
    queryKey: ["catchcompass_lake_map", id],
    queryFn: () =>
      fetch(`/app-api/lakes/${id}/map`).then(async (response) => {
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || "Lake map not found");
        return data;
      }),
    enabled: Boolean(id),
  });

  const weatherQuery = useQuery({
    queryKey: ["catchcompass_lake_map_weather", id],
    queryFn: () =>
      fetch(`/app-api/advisor/current-weather?lake_id=${encodeURIComponent(id ?? "")}`).then(
        (response) => (response.ok ? response.json() : { ok: false }),
      ),
    enabled: Boolean(id),
    staleTime: 15 * 60 * 1000,
  });

  const ownerControlsShared =
    mapQuery.data?.mapping_policy?.owner_direct_publish === true &&
    mapQuery.data?.mapping_policy?.lake_can_publish === true;

  const saveGpsPeg = useMutation({
    mutationFn: async ({
      name,
      swim,
    }: {
      name: string;
      swim: LakeMapSwim | null;
    }) => {
      const cleanName = name.trim();
      if (!cleanName) throw new Error("Enter a peg name first.");

      setGpsMessage("Getting your current GPS…");
      const gps = await currentGps();
      setGpsMessage(
        gps.accuracy_m == null
          ? ownerControlsShared
            ? "GPS locked. Updating the shared peg…"
            : "GPS locked. Saving private peg…"
          : ownerControlsShared
            ? `GPS locked to about ${Math.round(gps.accuracy_m)} m. Updating the shared peg…`
            : `GPS locked to about ${Math.round(gps.accuracy_m)} m. Saving private peg…`,
      );

      const swimId = privateSwimId(swim);
      const endpoint = ownerControlsShared
        ? `/app-api/lakes/${id}/map/owner-pegs`
        : swimId
          ? `/app-api/lakes/${id}/map/private-pegs/${swimId}`
          : `/app-api/lakes/${id}/map/private-pegs`;
      const response = await fetch(endpoint, {
        method: ownerControlsShared ? "POST" : swimId ? "PATCH" : "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          name: cleanName,
          ...gps,
        }),
      });
      const data = await response.json();
      if (!response.ok)
        throw new Error(data.error || "Could not save that peg.");
      return data;
    },
    onSuccess: async (data) => {
      setGpsMessage(
        data.accuracy_m == null
          ? data.shared_for_all
            ? "Shared peg updated for everyone who does not have a private override."
            : "Saved privately to your map."
          : data.shared_for_all
            ? `Shared peg updated · GPS accuracy about ${Math.round(Number(data.accuracy_m))} m.`
            : `Saved privately · GPS accuracy about ${Math.round(Number(data.accuracy_m))} m.`,
      );
      await queryClient.invalidateQueries({
        queryKey: ["catchcompass_lake_map", id],
      });
      setEditingSwim(null);
      setPegName("");
      setShowPersonalEditor(false);
      window.setTimeout(() => setGpsMessage(""), 3500);
    },
    onError: (error: any) => {
      setGpsMessage(error?.message || "Could not save that peg.");
    },
  });

  const openAddPersonal = () => {
    setEditingSwim(null);
    setPegName("");
    setGpsMessage("");
    setShowPersonalEditor(true);
  };

  const openEditPersonal = (swim: LakeMapSwim) => {
    setEditingSwim(swim);
    setPegName(swim.name);
    setGpsMessage("");
    setShowPersonalEditor(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (mapQuery.isLoading) {
    return <PageMessage text="Loading lake map…" />;
  }
  if (mapQuery.error || !mapQuery.data?.lake) {
    return <PageMessage text="That lake map could not be loaded." link="/lakes" />;
  }

  const data = mapQuery.data;
  const lake = data.lake;
  const swims = (Array.isArray(data.swims) ? data.swims : []) as LakeMapSwim[];
  const mapped = swims.filter(hasGps);
  const unmapped = swims.filter((swim) => !hasGps(swim));
  const highlight =
    requestedHighlight || data.active_session?.peg || null;
  const weather = weatherQuery.data?.ok ? weatherQuery.data : null;

  return (
    <div className="mx-auto max-w-[1040px] space-y-6 px-4 py-6 pb-28 md:py-8 md:pb-10">
      <header>
        <Link
          to={`/lakes/${lake.id}`}
          className="text-sm font-medium text-accent hover:underline"
        >
          {lake.name}
        </Link>
        <div className="mt-2 flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
          <div>
            <div className="flex items-center gap-2">
              <MapPinned className="size-6 text-accent" />
              <h1 className="text-2xl font-semibold tracking-tight text-primary">
                Interactive lake map
              </h1>
            </div>
            <p className="mt-1 text-sm text-secondary">
              {lake.location || "Verified and private peg mapping"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={openAddPersonal}>
              <Plus className="size-4" /> {ownerControlsShared ? "Add shared peg" : "Add personal peg"}
            </Button>
            <Button asChild variant="secondary">
              <Link to={`/scout?lake_id=${encodeURIComponent(String(lake.id))}`}>
                Scout
              </Link>
            </Button>
          </div>
        </div>
      </header>

      {showPersonalEditor ? (
        <section className="rounded-2xl border border-accent/40 bg-accent/5 p-4 sm:p-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-accent">
                {ownerControlsShared ? "Owner map control" : "Personal map"}
              </p>
              <h2 className="mt-1 text-lg font-semibold text-primary">
                {ownerControlsShared
                  ? editingSwim
                    ? `Move ${editingSwim.name} for everyone`
                    : "Add a shared peg"
                  : editingSwim
                    ? `Edit ${editingSwim.name} for me`
                    : "Add a private peg"}
              </h2>
              <p className="mt-1 text-sm leading-6 text-secondary">
                {ownerControlsShared
                  ? "Your GPS position becomes the authoritative shared position for this peg. Other anglers' private personal versions are never changed and still override the shared position on their own account."
                  : "This position is private to your account. If a shared peg has the same name, your private position will override it only on your map."}
              </p>
            </div>
            <button
              type="button"
              className="text-sm font-medium text-secondary hover:text-primary"
              onClick={() => {
                setShowPersonalEditor(false);
                setEditingSwim(null);
                setPegName("");
                setGpsMessage("");
              }}
            >
              Cancel
            </button>
          </div>

          <label className="mt-4 block">
            <span className="text-xs font-semibold uppercase tracking-wide text-secondary">
              Peg name
            </span>
            <input
              value={pegName}
              onChange={(event) => setPegName(event.target.value)}
              placeholder="e.g. Peg 7, Kamikaze, The Point"
              autoComplete="off"
              className="mt-1 min-h-11 w-full rounded-lg border border-border bg-background px-3 py-2 text-base text-primary outline-none focus:border-accent"
            />
          </label>

          <Button
            type="button"
            className="mt-4 w-full sm:w-auto"
            isLoading={saveGpsPeg.isPending}
            onClick={() =>
              saveGpsPeg.mutate({
                name: pegName,
                swim: editingSwim,
              })
            }
          >
            <Crosshair className="size-4" />
            {ownerControlsShared
              ? editingSwim
                ? "Move shared peg to my GPS"
                : "Save shared peg at my GPS"
              : editingSwim
                ? "Move to my current GPS"
                : "Save at my current GPS"}
          </Button>

          {gpsMessage ? (
            <p
              className={`mt-3 text-sm ${
                saveGpsPeg.isError ? "text-error" : "text-secondary"
              }`}
              role="status"
            >
              {gpsMessage}
            </p>
          ) : null}
        </section>
      ) : null}

      {weather ? (
        <section className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-raised px-4 py-3">
          <div className="flex size-9 items-center justify-center rounded-full bg-inset text-accent">
            <Wind className="size-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-secondary">
              Current wind
            </p>
            <p className="text-sm font-semibold text-primary">
              {[weather.wind_direction, weather.wind_speed_mph != null ? `${Math.round(Number(weather.wind_speed_mph))} mph` : null]
                .filter(Boolean)
                .join(" · ") || "Not available"}
            </p>
          </div>
          {weather.air_temp_c != null ? (
            <p className="ml-auto text-sm text-secondary">
              {Number(weather.air_temp_c).toFixed(1)}°C
              {weather.pressure_hpa != null
                ? ` · ${Math.round(Number(weather.pressure_hpa))} hPa`
                : ""}
            </p>
          ) : null}
        </section>
      ) : null}

      {data.active_session ? (
        <section className="rounded-xl border border-accent/40 bg-accent/5 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-accent">
            Live session
          </p>
          <p className="mt-1 text-sm text-primary">
            {data.active_session.peg
              ? `${data.active_session.peg} is highlighted on the map when GPS is available.`
              : "A live session is running on this lake."}
          </p>
          <Button asChild size="sm" className="mt-3">
            <Link to={`/sessions/${data.active_session.id}`}>Back to live session</Link>
          </Button>
        </section>
      ) : null}

      <section className="rounded-xl border border-border bg-raised p-4">
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
          <div>
            <p className="font-semibold text-primary">Walking the lake?</p>
            <p className="mt-1 text-sm text-secondary">
              Stand at a peg, name it, then save your current GPS. {ownerControlsShared
                ? "As owner, this updates the shared map for anglers without private overrides."
                : "It stays private to your account."}
            </p>
          </div>
          <Button type="button" variant="secondary" onClick={openAddPersonal}>
            <Crosshair className="size-4" /> {ownerControlsShared ? "Add shared peg at my GPS" : "Add peg at my GPS"}
          </Button>
        </div>
      </section>

      <InteractiveLakeMap
        latitude={lake.latitude}
        longitude={lake.longitude}
        swims={swims}
        highlightName={highlight}
      />

      <section className="grid gap-3 sm:grid-cols-3">
        <Metric label="Mapped GPS pegs" value={mapped.length} />
        <Metric
          label="Verified/shared"
          value={
            swims.filter(
              (swim) =>
                swim.source === "verified_owner" ||
                swim.source === "verified_community" ||
                swim.source === "verified_seed",
            ).length
          }
        />
        <Metric
          label="Private to you"
          value={
            swims.filter(
              (swim) =>
                swim.source === "private" ||
                swim.source === "pending_review" ||
                swim.source === "review_rejected",
            ).length
          }
        />
      </section>

      {mapped.length ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-primary">Mapped pegs</h2>
            <p className="mt-1 text-sm text-secondary">
              Tap the labels on the map for GPS details.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {mapped.map((swim) => (
              <div
                key={swim.id}
                className={`rounded-lg border p-3 ${
                  highlight &&
                  swim.name.trim().toLowerCase() ===
                    String(highlight).trim().toLowerCase()
                    ? "border-accent bg-accent/5"
                    : "border-border bg-raised"
                }`}
              >
                <p className="font-semibold text-primary">{swim.name}</p>
                <p className="mt-1 text-xs text-secondary">
                  {swimSourceLabel(swim.source)}
                </p>
                {swim.accuracy_m != null ? (
                  <p className="mt-1 text-xs text-tertiary">
                    GPS accuracy about {Math.round(Number(swim.accuracy_m))} m
                  </p>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="tertiary"
                  className="mt-2"
                  onClick={() => openEditPersonal(swim)}
                >
                  <Pencil className="size-3.5" />
                  {ownerControlsShared
                    ? "Move shared peg"
                    : swim.can_edit_personal
                      ? "Edit my peg"
                      : "Edit for me"}
                </Button>
              </div>
            ))}
          </div>
        </section>
      ) : (
        <section className="rounded-xl border border-dashed border-border p-6 text-center">
          <p className="font-semibold text-primary">No peg GPS points yet</p>
          <p className="mt-1 text-sm text-secondary">
            Add pegs from Scout while you are at the lake and the map will build itself over time.
          </p>
        </section>
      )}

      {unmapped.length ? (
        <section className="space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-primary">
              Known pegs still to map
            </h2>
            <p className="mt-1 text-sm text-secondary">
              These names are known to CatchCompass but do not yet have a GPS point.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {unmapped.map((swim) => (
              <button
                key={swim.id}
                type="button"
                className="rounded-full border border-border bg-raised px-3 py-1.5 text-sm text-secondary hover:border-accent hover:text-primary"
                title={`${swimSourceLabel(swim.source)} · tap to ${
                  ownerControlsShared ? "set the shared GPS position" : "map privately at your GPS"
                }`}
                onClick={() => openEditPersonal(swim)}
              >
                <Crosshair className="mr-1 inline size-3.5" />
                {swim.name}
              </button>
            ))}
          </div>
        </section>
      ) : null}

      <section className="rounded-xl border border-border bg-inset p-4 text-sm leading-6 text-secondary">
        {ownerControlsShared ? (
          <>
            <strong className="text-primary">Owner authority:</strong> your peg GPS edits update
            the shared lake map. Anglers who do not have a private version of that peg will see
            your position. Their private personal pegs are never edited and continue to override
            the shared position on their own account.
          </>
        ) : (
          <>
            <strong className="text-primary">Personal map privacy:</strong> pegs added or edited
            from this page stay private to your account. They never alter another member's map.
          </>
        )}
      </section>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-xl border border-border bg-raised p-4">
      <p className="text-2xl font-semibold text-primary">{value}</p>
      <p className="mt-1 text-xs text-secondary">{label}</p>
    </div>
  );
}

function PageMessage({ text, link }: { text: string; link?: string }) {
  return (
    <div className="mx-auto max-w-[760px] px-4 py-10 text-center">
      <p className="text-sm text-secondary">{text}</p>
       {link ? (
        <Link to={link} className="mt-3 inline-block text-sm font-medium text-accent hover:underline">
          Back
        </Link>
      ) : null}
    </div>
  );
}
